import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { referralApi, ReferralInfo } from '../services/referralApi';
import { useAuth } from '../context/AuthContext';

const fetchReferralInfo = async (): Promise<ReferralInfo> => {
  try {
    return await referralApi.getReferralInfo();
  } catch (error) {
    console.error('Ошибка при загрузке реферальной информации:', error);
    // Возвращаем дефолтные значения при ошибке
    return {
      personalPromoCode: 'Загрузка...',
      referralLink: '',
      balance: 0,
      program: 'Personal',
      canWithdraw: false,
      minWithdrawalAmount: 3000
    };
  }
};

export const useReferral = () => {
  const { token, profile } = useAuth();

  return useQuery({
    queryKey: ['referral', token],
    queryFn: fetchReferralInfo,
    enabled: !!(token && profile),
    staleTime: 5 * 60 * 1000, // 5 минут
    gcTime: 10 * 60 * 1000, // 10 минут
    retry: 2,
    // Обновляем кеш при фокусе окна
    refetchOnWindowFocus: true,
    // Обновляем кеш при переподключении к интернету
    refetchOnReconnect: true,
  });
};

// Хук для мутаций реферальной программы
export const useReferralMutations = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const applyForProfessional = useMutation({
    mutationFn: async (data: { phoneNumber: string; message: string }) => {
      return await referralApi.applyForProfessionalProgram({ 
        phoneNumber: data.phoneNumber, 
        message: data.message 
      });
    },
    onSuccess: () => {
      // Обновляем кеш реферальной программы
      queryClient.invalidateQueries({ 
        queryKey: referralQueryKey(token!) 
      });
      console.log('✅ Professional application sent, cache updated');
    },
    onError: (error: any) => {
      console.error('❌ Failed to apply for professional program:', error);
    }
  });

  const requestWithdrawal = useMutation({
    mutationFn: async (amount: number) => {
      return await referralApi.createWithdrawalRequest({ amount });
    },
    onSuccess: () => {
      // Обновляем кеш реферальной программы (баланс изменился)
      queryClient.invalidateQueries({ 
        queryKey: referralQueryKey(token!) 
      });
      console.log('✅ Withdrawal request sent, cache updated');
    },
    onError: (error: any) => {
      console.error('❌ Failed to request withdrawal:', error);
    }
  });

  const refreshBalance = useMutation({
    mutationFn: async () => {
      return await referralApi.getReferralInfo();
    },
    onSuccess: (data: ReferralInfo) => {
      // Сразу обновляем кеш новыми данными
      queryClient.setQueryData(referralQueryKey(token!), data);
      console.log('✅ Referral balance refreshed');
    },
    onError: (error: any) => {
      console.error('❌ Failed to refresh balance:', error);
    }
  });

  return {
    applyForProfessional,
    requestWithdrawal,
    refreshBalance
  };
};

// Экспортируем ключ запроса для инвалидации
export const referralQueryKey = (token: string) => ['referral', token]; 